import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { QRCodeCanvas } from "qrcode.react";
import { ChevronLeft, ChevronRight, LineChart, QrCode, TrendingUp } from "lucide-react";

const SHOTS = [
  { src: "/store/play_1_dashboard.png", cap: "Panel del día con sala de espera" },
  { src: "/store/play_2_pacientes.png", cap: "Expedientes ilimitados" },
  { src: "/store/play_3_expediente.png", cap: "Historia clínica NOM-004" },
  { src: "/store/play_4_graficas.png", cap: "Percentiles CDC/OMS automáticos" },
  { src: "/store/play_5_recetas.png", cap: "Recetas en PDF con QR" },
];

function AnimatedChart() {
  return (
    <div className="bg-white rounded-3xl border border-stone-100 shadow-lg p-6 overflow-hidden" data-testid="demo-chart">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-9 h-9 rounded-xl bg-brand-sage/15 text-brand-sageDark grid place-items-center"><LineChart size={18} /></div>
        <div>
          <p className="font-outfit font-bold text-brand-ink text-sm">Curva de crecimiento</p>
          <p className="text-[11px] text-brand-ink/50">Peso para la edad · niña</p>
        </div>
      </div>
      <svg viewBox="0 0 320 180" className="w-full h-auto">
        {[0, 1, 2, 3].map((i) => (
          <line key={i} x1="30" y1={30 + i * 40} x2="310" y2={30 + i * 40} stroke="#eee" strokeWidth="1" />
        ))}
        <line x1="30" y1="20" x2="30" y2="150" stroke="#eee" strokeWidth="1" />
        {[
          { d: "M30,140 C90,120 180,90 310,55", c: "#CBD5C0", w: 1.5 },
          { d: "M30,120 C90,96 180,66 310,32", c: "#7A9B76", w: 3 },
          { d: "M30,150 C90,135 180,110 310,80", c: "#CBD5C0", w: 1.5 },
        ].map((p, i) => (
          <motion.path key={i} d={p.d} fill="none" stroke={p.c} strokeWidth={p.w} strokeLinecap="round"
            initial={{ pathLength: 0, opacity: 0 }} whileInView={{ pathLength: 1, opacity: 1 }}
            viewport={{ once: false, amount: 0.4 }} transition={{ duration: 1.6, delay: 0.15 + i * 0.15, ease: "easeInOut" }} />
        ))}
        {[[80, 118], [160, 92], [240, 78], [300, 60]].map(([cx, cy], i) => (
          <g key={i}>
            <motion.circle cx={cx} cy={cy} r="8" fill="#C4623F" opacity="0.25"
              initial={{ scale: 0 }} whileInView={{ scale: [0, 1.4, 1] }} viewport={{ once: false, amount: 0.4 }}
              transition={{ delay: 1.1 + i * 0.18, duration: 1.2, repeat: Infinity, repeatDelay: 1.4 }} style={{ transformOrigin: `${cx}px ${cy}px` }} />
            <motion.circle cx={cx} cy={cy} r="5" fill="#C4623F" stroke="#fff" strokeWidth="2"
              initial={{ scale: 0, opacity: 0 }} whileInView={{ scale: 1, opacity: 1 }} viewport={{ once: false, amount: 0.4 }}
              transition={{ delay: 1.1 + i * 0.18, type: "spring", stiffness: 300 }} style={{ transformOrigin: `${cx}px ${cy}px` }} />
          </g>
        ))}
      </svg>
      <div className="flex items-center gap-1.5 mt-2 text-[11px] font-semibold text-brand-sageDark">
        <TrendingUp size={13} /> Percentil P50 · dentro de rango
      </div>
    </div>
  );
}

function AnimatedRx() {
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: false, amount: 0.3 }}
      transition={{ duration: 0.5 }} className="bg-white rounded-3xl border border-stone-100 shadow-lg p-6" data-testid="demo-rx">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-9 h-9 rounded-xl bg-brand-terracotta/10 text-brand-terracotta grid place-items-center"><QrCode size={18} /></div>
        <p className="font-outfit font-bold text-brand-ink text-sm">Receta lista para compartir</p>
      </div>
      <div className="flex gap-4 items-center">
        <div className="flex-1 space-y-2">
          {[90, 70, 80, 55].map((w, i) => (
            <motion.div key={i} initial={{ width: 0, opacity: 0 }} whileInView={{ width: `${w}%`, opacity: 1 }} viewport={{ once: false, amount: 0.3 }}
              transition={{ delay: 0.3 + i * 0.15, duration: 0.5 }} className="h-2.5 rounded-full bg-stone-200" />
          ))}
        </div>
        <motion.div animate={{ scale: [1, 1.04, 1] }} transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
          className="relative bg-white p-2 rounded-xl border-2 border-brand-terracotta/20 shrink-0 overflow-hidden">
          <QRCodeCanvas value="https://expedienteelectronicoclinped.com" size={78} fgColor="#C4623F" level="M" />
          <motion.div className="absolute left-1 right-1 h-6 bg-gradient-to-b from-brand-terracotta/40 to-transparent rounded"
            animate={{ top: ["4px", "72px", "4px"] }} transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }} />
        </motion.div>
      </div>
      <p className="text-[11px] text-brand-ink/50 mt-3">El tutor la abre y descarga en su teléfono con un escaneo.</p>
    </motion.div>
  );
}

export default function LandingDemo() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % SHOTS.length), 3500);
    return () => clearInterval(t);
  }, []);
  const go = (d) => setIdx((i) => (i + d + SHOTS.length) % SHOTS.length);

  return (
    <section id="demo" className="px-5 md:px-8 py-16 md:py-24 scroll-mt-20" data-testid="demo-section">
      <div className="max-w-lg mx-auto text-center mb-10">
        <p className="uppercase text-xs tracking-[0.25em] font-bold text-brand-terracotta mb-3">Míralo en acción</p>
        <h2 className="font-outfit font-bold text-3xl md:text-4xl tracking-tight">Rápido, visual y sin papel</h2>
        <p className="mt-3 text-brand-ink/60">Percentiles que se dibujan solos y recetas que compartes por QR en segundos.</p>
      </div>

      <div className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-6 items-center">
        <div className="space-y-6"><AnimatedChart /><AnimatedRx /></div>

        <div className="relative" data-testid="demo-carousel">
          <div className="relative mx-auto max-w-[300px] rounded-[2rem] border-[6px] border-brand-ink bg-brand-ink shadow-2xl overflow-hidden aspect-[9/16]">
            {SHOTS.map((s, i) => (
              <motion.img key={s.src} src={s.src} alt={s.cap} loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
                initial={false} animate={{ opacity: i === idx ? 1 : 0 }} transition={{ duration: 0.6 }} />
            ))}
          </div>
          <div className="mt-4 flex items-center justify-center gap-4">
            <button data-testid="demo-prev" onClick={() => go(-1)} className="w-9 h-9 rounded-full bg-white border border-stone-200 grid place-items-center text-brand-ink hover:bg-brand-terracotta hover:text-white transition-all"><ChevronLeft size={18} /></button>
            <div className="flex gap-1.5">
              {SHOTS.map((_, i) => (
                <button key={i} data-testid={`demo-dot-${i}`} onClick={() => setIdx(i)}
                  className={`h-2 rounded-full transition-all ${i === idx ? "w-6 bg-brand-terracotta" : "w-2 bg-stone-300"}`} />
              ))}
            </div>
            <button data-testid="demo-next" onClick={() => go(1)} className="w-9 h-9 rounded-full bg-white border border-stone-200 grid place-items-center text-brand-ink hover:bg-brand-terracotta hover:text-white transition-all"><ChevronRight size={18} /></button>
          </div>
          <p className="text-center text-sm font-semibold text-brand-ink/70 mt-3" data-testid="demo-caption">{SHOTS[idx].cap}</p>
        </div>
      </div>
    </section>
  );
}
